/**
 * Plain-text maths, shown the way a printed page shows it (founder, 2026-09-15:
 * option 2 of the maths display plan; 2026-09-17: stacked fractions and
 * radicals through `mathNodes`, drawn by the web app's MathText).
 *
 * The engine and the SAT bank write maths as plain text (engine voice register,
 * maths): a hyphen for minus, x^2, sqrt(5), 3 * 4, <= and >=, pi, 45 degrees.
 * `prettyMath` swaps those for the characters a page uses and changes nothing
 * else. It never changes a digit, so what a learner reads is what they type
 * back; fractions stay a/b in the string for the same reason, and a client
 * that can stack them asks `mathNodes` for the pieces. Hyphens inside words
 * (x-intercept), dates and markdown bullets are left alone. Running
 * `prettyMath` on its own output changes nothing.
 */

const MINUS = "−";
const SUPERSCRIPT: Record<string, string> = {
  "0": "⁰",
  "1": "¹",
  "2": "²",
  "3": "³",
  "4": "⁴",
  "5": "⁵",
  "6": "⁶",
  "7": "⁷",
  "8": "⁸",
  "9": "⁹",
  "+": "⁺",
  "-": "⁻",
  "−": "⁻",
  n: "ⁿ",
  x: "ˣ",
};
const SUP = "\\u00B2\\u00B3\\u00B9\\u2070-\\u209F\\u02E3";
/** Characters that end an operand: word characters, a closing bracket, a superscript. */
const OPERAND_END = `\\w)${SUP}`;

const toSuperscript = (exp: string): string | null => ([...exp].every((c) => SUPERSCRIPT[c]) ? [...exp].map((c) => SUPERSCRIPT[c]).join("") : null);

export function prettyMath(text: string): string {
  return (
    text
      // x^(n+1) and x^{-5} when every character has a superscript form.
      .replace(new RegExp(`(?<=[${OPERAND_END}])\\^(?:\\(([-+${MINUS}0-9nx]+)\\)|\\{([-+${MINUS}0-9nx]+)\\})`, "g"), (m, round: string | undefined, curly: string | undefined) => toSuperscript(round ?? curly ?? "") ?? m)
      // x^2, 10^-3, x^−5, 2^n; a decimal exponent (x^2.5) is left as written, a sentence's full stop is not a decimal
      // point (quality log Run 7: a hint's "x^-5." stayed raw while the stem showed x⁻⁵).
      .replace(new RegExp(`(?<=[${OPERAND_END}])\\^([-+${MINUS}]?\\d+|[nx])(?!\\d|\\.\\d)`, "g"), (m, exp: string) => toSuperscript(exp) ?? m)
      // sqrt(5) and sqrt(x) lose their brackets; sqrt(x + 1) keeps them. A coefficient may touch it (10sqrt(2)), a
      // word may not (quality log Runs 4 to 6: "10sqrt2" and "sqrt(" beside a real √ in one choice set).
      .replace(/(?<![A-Za-z])sqrt\(([^()]*)\)/g, (_m, inner: string) => (/^[\w.]+$/.test(inner) ? `√${inner}` : `√(${inner})`))
      // sqrt2, 10sqrt2 and sqrtx: a bare number or a single letter under the root.
      .replace(/(?<![A-Za-z])sqrt(\d+(?:\.\d+)?|[A-Za-z](?![A-Za-z]))/g, "√$1")
      // 3 * 4 and 3*x between operands; markdown bold (**) never matches.
      .replace(new RegExp(`(?<=[${OPERAND_END}]) ?\\*(?!\\*) ?(?=[\\w(\\u221A])`, "g"), " × ")
      .replace(/<=/g, "≤")
      .replace(/>=/g, "≥")
      .replace(/!=/g, "≠")
      // pi as a number: 2pi, pi/4, sin(pi); never inside a word.
      .replace(/(?<![A-Za-z])pi(?![A-Za-z])/g, "π")
      // 45 degrees, a 90-degree angle: a number's degrees get the sign; "a degree in maths" keeps the word.
      .replace(/(\d)(?: |-)?degrees?\b/g, "$1°")
      // 11 - 10, x - y and 8x - 5: a spaced hyphen between a number, a variable or a bracket on each side.
      // A variable is a lone letter (x) or a letter carrying a coefficient (8x), never the last letter of a word,
      // so "rock - solid" keeps its hyphen while "8x - 5" gets the minus sign (2026-09-17, seen on a concept card).
      .replace(new RegExp(`(?<=(?:\\d|\\)|(?:\\b|\\d)[A-Za-z]|[${SUP}]|°|π) )-(?= (?:[\\d(\\u221A${MINUS}-]|\\d*[A-Za-z]\\b|π))`, "g"), MINUS)
      // -22, -x, -(3): a hyphen at the start or after a space, bracket, equals sign or operator, before a number, a single letter or a bracket.
      .replace(new RegExp(`(?<=^|[\\s(=,+\\u00D7\\u2264\\u2265<>\\[${MINUS}])-(?=\\d|\\.\\d|[A-Za-z]\\b|\\(|π)`, "gm"), MINUS)
  );
}

/** A run of the prettified text: plain, a stacked fraction, or a radical over its radicand. */
export type MathNode = { kind: "text"; text: string } | { kind: "frac"; num: string; den: string } | { kind: "root"; inner: string };

const strip = (s: string): string => (s.startsWith("(") && s.endsWith(")") ? s.slice(1, -1).trim() : s);

/**
 * A numerator: a bracketed group, a number with an optional coefficient letter
 * and exponent (3, 2.5, 2x, x²), or a single letter with an exponent. A
 * denominator: a bracketed group, a number, or a single letter with an
 * exponent, so 3/4x stays inline (ambiguous) and 3/(4x) stacks.
 */
const NUM = `\\([^()]+\\)|\\d+(?:\\.\\d+)?[A-Za-z]?[${SUP}]*|[A-Za-z][${SUP}]*`;
const DEN = `\\([^()]+\\)|\\d+(?:\\.\\d+)?|[A-Za-z][${SUP}]*`;
// A full stop ends a radicand or a denominator unless a digit follows it (a decimal point): "1/x^5." and "√200." at
// the end of a sentence stack and bar like the same maths mid-sentence (quality log Run 7).
const PIECE = new RegExp(`√\\(([^()]+)\\)|√((?:[\\w${SUP}]|\\.(?=\\d))+)|(?<![\\w./])(${NUM})/(${DEN})(?![\\w/(]|\\.\\d)`, "g");

/**
 * Splits text into runs a client can typeset: fractions stacked where they are
 * unambiguous (at least one side is a number or a bracketed group, so m/s and
 * and/or stay words), radicals with their radicand, everything else as text
 * already passed through `prettyMath`. Digits are never changed.
 */
export function mathNodes(text: string): MathNode[] {
  const pretty = prettyMath(text);
  const out: MathNode[] = [];
  let last = 0;
  for (const m of pretty.matchAll(PIECE)) {
    const [whole, rootGroup, rootPlain, num, den] = m;
    let node: MathNode | null = null;
    if (rootGroup !== undefined) node = { kind: "root", inner: rootGroup };
    else if (rootPlain !== undefined) node = { kind: "root", inner: rootPlain };
    // A leading zero is a date (09/15), never a fraction.
    else if (num !== undefined && den !== undefined && (/\d|^\(/.test(num) || /\d|^\(/.test(den)) && !/^0\d/.test(num) && !/^0\d/.test(den)) node = { kind: "frac", num: strip(num), den: strip(den) };
    if (!node) continue;
    if (m.index > last) out.push({ kind: "text", text: pretty.slice(last, m.index) });
    out.push(node);
    last = m.index + whole.length;
  }
  if (last < pretty.length) out.push({ kind: "text", text: pretty.slice(last) });
  return out;
}

/**
 * A figure with its human-read labels typeset the way the text is: series
 * names, axis names, marks, angle and circle labels, free labels, table cells
 * and the caption. Expressions and point names are left exactly as written,
 * because the renderer parses them.
 */
export function prettyFigure<F extends { kind: string; title?: string }>(figure: F): F {
  const p = (s: string | undefined) => (s === undefined ? undefined : prettyMath(s));
  const strings = (xs: string[] | undefined) => xs?.map(prettyMath);
  const out: Record<string, unknown> = { ...figure };
  for (const key of ["title", "x_label", "y_label"]) if (typeof out[key] === "string") out[key] = prettyMath(out[key] as string);
  if (Array.isArray(out["series"])) out["series"] = (out["series"] as Array<{ label?: string }>).map((s) => ({ ...s, ...(s.label !== undefined ? { label: prettyMath(s.label) } : {}) }));
  if (Array.isArray(out["categories"])) out["categories"] = strings(out["categories"] as string[]);
  if (Array.isArray(out["columns"])) out["columns"] = strings(out["columns"] as string[]);
  if (Array.isArray(out["rows"])) out["rows"] = (out["rows"] as Array<Array<string | number>>).map((r) => r.map((c) => (typeof c === "string" ? prettyMath(c) : c)));
  if (Array.isArray(out["marks"])) out["marks"] = (out["marks"] as Array<{ label?: string }>).map((m) => ({ ...m, ...(m.label !== undefined ? { label: p(m.label) } : {}) }));
  if (Array.isArray(out["circles"])) out["circles"] = (out["circles"] as Array<{ label?: string }>).map((c) => ({ ...c, ...(c.label !== undefined ? { label: p(c.label) } : {}) }));
  if (Array.isArray(out["angles"])) out["angles"] = (out["angles"] as Array<{ label?: string }>).map((a) => ({ ...a, ...(a.label !== undefined ? { label: p(a.label) } : {}) }));
  if (Array.isArray(out["labels"])) out["labels"] = (out["labels"] as Array<{ text: string }>).map((l) => ({ ...l, text: prettyMath(l.text) }));
  return out as F;
}
